import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import {
  AppBar,
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import {
  ChatBubbleOutline,
  LoginSharp,
  LogoutSharp,
  Menu as MenuIcon,
  PeopleAlt as PeopleAltIcon,
  Storage as StorageIcon,
  TableChart as TableChartIcon,
  AccountCircle,
  Settings,
} from "@mui/icons-material";

import { LOGO_URL } from "config";
import { useLogoutMutation } from "features/auth/auth";
import { useAppSelector } from "features/store";

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [logout] = useLogoutMutation();

  const location = useLocation();
  const navigate = useNavigate();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const isActive = useMemo(
    () => ({
      integrations: location.pathname.startsWith("/integrations"),
      storage: location.pathname === "/",
      chat: location.pathname === "/chat",
      userManagement: location.pathname === "/user-management",
    }),
    [location.pathname],
  );

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const onLogout = () => {
    handleMenuClose();
    logout()
      .unwrap()
      .then(() => navigate("/login"))
      .catch((error) => console.error("Logout failed:", error));
  };

  const handleAccountSettings = () => {
    handleMenuClose();
    navigate("/account-settings");
  };

  const closeDrawerAndNavigate = (path: string) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const navBtnSx = (active: boolean) => ({
    textTransform: "none",
    borderRadius: 999,
    px: 1.5,
    fontWeight: active ? 800 : 600,
    color: active ? "primary.main" : "text.primary",
    bgcolor: active ? "rgba(31,93,107,0.08)" : "transparent",
    "&:hover": {
      bgcolor: active ? "rgba(31,93,107,0.12)" : "rgba(0,0,0,0.04)",
    },
  });

  const drawerContent = (
    <Box sx={{ width: 280, pt: 2, pb: 2 }} role="presentation">
      <List>
        {((user?.is_calibrate_admin || user?.is_integration_user) || user?.is_superuser) && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate("/integrations")}
            selected={isActive.integrations}
            sx={{ py: 1.25, "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" } }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <TableChartIcon color={isActive.integrations ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText primary="Integrations" primaryTypographyProps={{ fontWeight: isActive.integrations ? 800 : 600 }} />
          </ListItemButton>
        )}
        {(user?.is_calibrate_admin || user?.is_superuser) && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate("/user-management")}
            selected={isActive.userManagement}
            sx={{ py: 1.25, "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" } }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <PeopleAltIcon color={isActive.userManagement ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText primary="User Management" primaryTypographyProps={{ fontWeight: isActive.userManagement ? 800 : 600 }} />
          </ListItemButton>
        )}
        <ListItemButton
          onClick={() => closeDrawerAndNavigate("/")}
          selected={isActive.storage}
          sx={{ py: 1.25, "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" } }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            <StorageIcon color={isActive.storage ? "primary" : "inherit"} />
          </ListItemIcon>
          <ListItemText primary="Data Storage" primaryTypographyProps={{ fontWeight: isActive.storage ? 800 : 600 }} />
        </ListItemButton>
        {user?.is_chat_bot_allowed && (
          <ListItemButton
            onClick={() => closeDrawerAndNavigate("/chat")}
            selected={isActive.chat}
            sx={{ py: 1.25, "&.Mui-selected": { bgcolor: "rgba(31,93,107,0.08)" } }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ChatBubbleOutline color={isActive.chat ? "primary" : "inherit"} />
            </ListItemIcon>
            <ListItemText primary="AI Agent" primaryTypographyProps={{ fontWeight: isActive.chat ? 800 : 600 }} />
          </ListItemButton>
        )}
        <Divider sx={{ my: 1 }} />
        <ListItemButton onClick={() => { setDrawerOpen(false); handleAccountSettings(); }} sx={{ py: 1.25 }}>
          <ListItemIcon sx={{ minWidth: 40 }}><Settings /></ListItemIcon>
          <ListItemText primary="Account settings" primaryTypographyProps={{ fontWeight: 600 }} />
        </ListItemButton>
        <ListItemButton
          onClick={() => { setDrawerOpen(false); onLogout(); }}
          sx={{ py: 1.25, color: "error.main", "&:hover": { bgcolor: "rgba(211,47,47,0.08)" } }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "error.main" }}><LogoutSharp /></ListItemIcon>
          <ListItemText primary="Logout" primaryTypographyProps={{ fontWeight: 700, color: "error.main" }} />
        </ListItemButton>
      </List>
    </Box>
  );

  return (
    <>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(12px)",
          borderBottom: "1px solid rgba(0,0,0,0.08)",
          color: "text.primary",
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, md: 80 }, gap: 2 }}>
          {isMobile && isAuthenticated && user !== null && location.pathname !== "/reset-password" && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="open menu"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 0.5 }}
            >
              <MenuIcon />
            </IconButton>
          )}
          <Link to="/" className="flex cursor-pointer items-center" style={{ display: "flex" }}>
            <Box
              component="img"
              src={LOGO_URL}
              alt="Calibrate HCM"
              sx={{ height: { xs: 40, md: 56 }, my: 1, cursor: "pointer" }}
            />
          </Link>
          <Box sx={{ flexGrow: 1 }} />

          {!isMobile && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {isAuthenticated && user !== null && location.pathname !== "/reset-password" ? (
                <>
                  {((user?.is_calibrate_admin || user?.is_integration_user) || user?.is_superuser) && (
                    <>
                      <Button
                        startIcon={<TableChartIcon />}
                        onClick={() => navigate("/integrations")}
                        sx={navBtnSx(isActive.integrations)}
                      >
                        Integrations
                      </Button>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    </>
                  )}
                  {(user?.is_calibrate_admin || user?.is_superuser) && (
                    <>
                      <Button
                        startIcon={<PeopleAltIcon />}
                        onClick={() => navigate("/user-management")}
                        sx={navBtnSx(isActive.userManagement)}
                      >
                        User Management
                      </Button>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    </>
                  )}

                  <Button
                    startIcon={<StorageIcon />}
                    onClick={() => navigate("/")}
                    sx={navBtnSx(isActive.storage)}
                  >
                    Data Storage
                  </Button>

                  <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                  {user?.is_chat_bot_allowed && (
                    <>
                      <Button
                        startIcon={<ChatBubbleOutline />}
                        onClick={() => navigate("/chat")}
                        sx={navBtnSx(isActive.chat)}
                      >
                        AI Agent
                      </Button>
                      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
                    </>
                  )}

                  <IconButton
                onClick={handleMenuOpen}
                aria-controls={menuOpen ? "user-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
                size="small"
                sx={{
                  ml: 0.25,
                  p: 0.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: menuOpen ? "primary.main" : "divider",
                  bgcolor: "background.paper",
                  boxShadow: menuOpen ? "0px 10px 30px rgba(0,0,0,0.10)" : "none",
                  transition: "border-color 140ms ease, box-shadow 140ms ease, background-color 140ms ease",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    fontWeight: 900,
                    color: "primary.main",
                    bgcolor: "rgba(31,93,107,0.10)",
                  }}
                >
                  {user?.email && user.email.length > 0 ? (
                    user.email[0].toUpperCase()
                  ) : (
                    <AccountCircle fontSize="small" />
                  )}
                </Box>
              </IconButton>
              <Menu
                id="user-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{
                  dense: true,
                  sx: { p: 0.5 },
                }}
                sx={{
                  "& .MuiPaper-root": {
                    mt: 1.25,
                  },
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    minWidth: 220,
                    borderRadius: 1.25,
                    border: "1px solid",
                    borderColor: "rgba(0,0,0,0.10)",

                    bgcolor: "rgba(255,255,255,0.68) !important",
                    backgroundImage: "none !important",
                    backdropFilter: "blur(12px)",
                    WebkitBackdropFilter: "blur(12px)",

                    boxShadow: "0px 18px 60px rgba(0,0,0,0.16)",
                    overflow: "hidden",
                    mt: 20
                  },
                }}
              >
                <MenuItem
                  onClick={handleAccountSettings}
                  sx={{
                    px: 1.25,
                    py: 0.9,
                    borderRadius: 1,
                    mx: 0.25,
                    my: 0.25,
                    "&:hover": { bgcolor: "rgba(31,93,107,0.08)" },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "primary.main",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: "text.secondary" }}>
                    <Settings sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Account settings"
                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: 800 }}
                  />
                </MenuItem>

                <Divider sx={{ my: 0.5 }} />

                <MenuItem
                  onClick={onLogout}
                  sx={{
                    px: 1.25,
                    py: 0.95,
                    borderRadius: 1,
                    mx: 0.25,
                    my: 0.25,
                    "&:hover": { bgcolor: "rgba(211,47,47,0.08)" },
                    "&:focus-visible": {
                      outline: "2px solid",
                      outlineColor: "error.main",
                      outlineOffset: 2,
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 34, color: "error.main" }}>
                    <LogoutSharp sx={{ fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary="Logout"
                    primaryTypographyProps={{ fontSize: 13.5, fontWeight: 900, color: "error.main" }}
                  />
                </MenuItem>
              </Menu>
            </>
          ) : (
            location.pathname !== "/login" &&
            location.pathname !== "/reset-password" &&
            location.pathname !== "/integrations" && (
              <Button
                startIcon={<LoginSharp />}
                href="/login"
                variant="contained"
                color="primary"
                sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800 }}
              >
                Login
              </Button>
            )
          )}
            </Box>
          )}

          {isMobile && isAuthenticated && user !== null && location.pathname !== "/reset-password" && (
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <IconButton
                onClick={handleMenuOpen}
                aria-controls={menuOpen ? "user-menu" : undefined}
                aria-haspopup="true"
                aria-expanded={menuOpen ? "true" : undefined}
                size="small"
                sx={{
                  p: 0.5,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: menuOpen ? "primary.main" : "divider",
                  bgcolor: "background.paper",
                  "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
                }}
              >
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1.5,
                    display: "grid",
                    placeItems: "center",
                    fontSize: 13,
                    fontWeight: 900,
                    color: "primary.main",
                    bgcolor: "rgba(31,93,107,0.10)",
                  }}
                >
                  {user?.email && user.email.length > 0 ? (
                    user.email[0].toUpperCase()
                  ) : (
                    <AccountCircle fontSize="small" />
                  )}
                </Box>
              </IconButton>
              <Menu
                id="user-menu-mobile"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{ dense: true, sx: { p: 0.5 } }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    minWidth: 220,
                    borderRadius: 1.25,
                    border: "1px solid",
                    borderColor: "rgba(0,0,0,0.10)",
                    bgcolor: "rgba(255,255,255,0.68) !important",
                    backgroundImage: "none !important",
                    backdropFilter: "blur(12px)",
                    boxShadow: "0px 18px 60px rgba(0,0,0,0.16)",
                  },
                }}
              >
                <MenuItem onClick={handleAccountSettings} sx={{ "&:hover": { bgcolor: "rgba(31,93,107,0.08)" } }}>
                  <ListItemIcon sx={{ minWidth: 34 }}><Settings sx={{ fontSize: 18 }} /></ListItemIcon>
                  <ListItemText primary="Account settings" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 800 }} />
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem onClick={onLogout} sx={{ "&:hover": { bgcolor: "rgba(211,47,47,0.08)" } }}>
                  <ListItemIcon sx={{ minWidth: 34, color: "error.main" }}><LogoutSharp sx={{ fontSize: 18 }} /></ListItemIcon>
                  <ListItemText primary="Logout" primaryTypographyProps={{ fontSize: 13.5, fontWeight: 900, color: "error.main" }} />
                </MenuItem>
              </Menu>
            </Box>
          )}
          {isMobile && !isAuthenticated && location.pathname !== "/login" && location.pathname !== "/reset-password" && (
            <Button
              startIcon={<LoginSharp />}
              href="/login"
              variant="contained"
              color="primary"
              sx={{ textTransform: "none", borderRadius: 999, fontWeight: 800 }}
            >
              Login
            </Button>
          )}
        </Toolbar>
      </AppBar>

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          "& .MuiDrawer-paper": {
            borderRight: "1px solid rgba(0,0,0,0.08)",
            boxSizing: "border-box",
            width: 280,
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
};

export default Header;
